/**
 * Platform Guide - Public presentation page
 * Shows complete workflow for all user types
 * Supports EN/NO languages
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import {
  Building2,
  Users,
  HardHat,
  UserCheck,
  ClipboardList,
  CheckCircle2,
  Briefcase,
  FileText,
  Bell,
  Search,
  Star,
  Shield,
  TrendingUp,
  Play,
  Home
} from "lucide-react";

// Icon mapping for steps
const iconMap = {
  ClipboardList,
  Bell,
  FileText,
  UserCheck,
  HardHat,
  Shield,
  CheckCircle2,
  Building2,
  TrendingUp,
  Home,
  Users,
  Briefcase,
  Search,
  Star
};

export default function PlatformGuide() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("project");

  // Define tabs with translations
  const tabs = [
    { id: "project", label: t("platformGuide.tabs.projectFlow"), icon: Briefcase, color: "text-[#23F7DD]" },
    { id: "client", label: t("platformGuide.tabs.forClients"), icon: Building2, color: "text-blue-400" },
    { id: "holding", label: t("platformGuide.tabs.forHolding"), icon: TrendingUp, color: "text-purple-400" },
    { id: "worker", label: t("platformGuide.tabs.forWorkers"), icon: HardHat, color: "text-orange-400" },
    { id: "teamleader", label: t("platformGuide.tabs.forTeamLeaders"), icon: UserCheck, color: "text-green-400" }
  ];

  // Define steps with translations
  const steps = {
    project: [
      { step: 1, title: t("platformGuide.projectFlow.step1Title"), description: t("platformGuide.projectFlow.step1Desc"), icon: ClipboardList, color: "text-blue-400", bg: "bg-blue-500/20" },
      { step: 2, title: t("platformGuide.projectFlow.step2Title"), description: t("platformGuide.projectFlow.step2Desc"), icon: Bell, color: "text-purple-400", bg: "bg-purple-500/20" },
      { step: 3, title: t("platformGuide.projectFlow.step3Title"), description: t("platformGuide.projectFlow.step3Desc"), icon: FileText, color: "text-orange-400", bg: "bg-orange-500/20" },
      { step: 4, title: t("platformGuide.projectFlow.step4Title"), description: t("platformGuide.projectFlow.step4Desc"), icon: Star, color: "text-yellow-400", bg: "bg-yellow-500/20" },
      { step: 5, title: t("platformGuide.projectFlow.step5Title"), description: t("platformGuide.projectFlow.step5Desc"), icon: HardHat, color: "text-green-400", bg: "bg-green-500/20" },
      { step: 6, title: t("platformGuide.projectFlow.step6Title"), description: t("platformGuide.projectFlow.step6Desc"), icon: Search, color: "text-cyan-400", bg: "bg-cyan-500/20" },
      { step: 7, title: t("platformGuide.projectFlow.step7Title"), description: t("platformGuide.projectFlow.step7Desc"), icon: CheckCircle2, color: "text-[#23F7DD]", bg: "bg-[#23F7DD]/20" }
    ],
    client: [
      { step: 1, title: t("platformGuide.client.step1Title"), description: t("platformGuide.client.step1Desc"), icon: Building2, details: t("platformGuide.client.step1Details", { returnObjects: true }) },
      { step: 2, title: t("platformGuide.client.step2Title"), description: t("platformGuide.client.step2Desc"), icon: ClipboardList, details: t("platformGuide.client.step2Details", { returnObjects: true }) },
      { step: 3, title: t("platformGuide.client.step3Title"), description: t("platformGuide.client.step3Desc"), icon: FileText, details: t("platformGuide.client.step3Details", { returnObjects: true }) },
      { step: 4, title: t("platformGuide.client.step4Title"), description: t("platformGuide.client.step4Desc"), icon: UserCheck, details: t("platformGuide.client.step4Details", { returnObjects: true }) },
      { step: 5, title: t("platformGuide.client.step5Title"), description: t("platformGuide.client.step5Desc"), icon: TrendingUp, details: t("platformGuide.client.step5Details", { returnObjects: true }) }
    ],
    holding: [
      { step: 1, title: t("platformGuide.holding.step1Title"), description: t("platformGuide.holding.step1Desc"), icon: Users, details: t("platformGuide.holding.step1Details", { returnObjects: true }) },
      { step: 2, title: t("platformGuide.holding.step2Title"), description: t("platformGuide.holding.step2Desc"), icon: UserCheck, details: t("platformGuide.holding.step2Details", { returnObjects: true }) },
      { step: 3, title: t("platformGuide.holding.step3Title"), description: t("platformGuide.holding.step3Desc"), icon: Briefcase, details: t("platformGuide.holding.step3Details", { returnObjects: true }) },
      { step: 4, title: t("platformGuide.holding.step4Title"), description: t("platformGuide.holding.step4Desc"), icon: FileText, details: t("platformGuide.holding.step4Details", { returnObjects: true }) },
      { step: 5, title: t("platformGuide.holding.step5Title"), description: t("platformGuide.holding.step5Desc"), icon: Star, details: t("platformGuide.holding.step5Details", { returnObjects: true }) }
    ],
    worker: [
      { step: 1, title: t("platformGuide.worker.step1Title"), description: t("platformGuide.worker.step1Desc"), icon: HardHat, details: t("platformGuide.worker.step1Details", { returnObjects: true }) },
      { step: 2, title: t("platformGuide.worker.step2Title"), description: t("platformGuide.worker.step2Desc"), icon: CheckCircle2, details: t("platformGuide.worker.step2Details", { returnObjects: true }) },
      { step: 3, title: t("platformGuide.worker.step3Title"), description: t("platformGuide.worker.step3Desc"), icon: Users, details: t("platformGuide.worker.step3Details", { returnObjects: true }) },
      { step: 4, title: t("platformGuide.worker.step4Title"), description: t("platformGuide.worker.step4Desc"), icon: Briefcase, details: t("platformGuide.worker.step4Details", { returnObjects: true }) },
      { step: 5, title: t("platformGuide.worker.step5Title"), description: t("platformGuide.worker.step5Desc"), icon: Star, details: t("platformGuide.worker.step5Details", { returnObjects: true }) }
    ],
    teamleader: [
      { step: 1, title: t("platformGuide.teamleader.step1Title"), description: t("platformGuide.teamleader.step1Desc"), icon: TrendingUp, details: t("platformGuide.teamleader.step1Details", { returnObjects: true }) },
      { step: 2, title: t("platformGuide.teamleader.step2Title"), description: t("platformGuide.teamleader.step2Desc"), icon: UserCheck, details: t("platformGuide.teamleader.step2Details", { returnObjects: true }) },
      { step: 3, title: t("platformGuide.teamleader.step3Title"), description: t("platformGuide.teamleader.step3Desc"), icon: Users, details: t("platformGuide.teamleader.step3Details", { returnObjects: true }) },
      { step: 4, title: t("platformGuide.teamleader.step4Title"), description: t("platformGuide.teamleader.step4Desc"), icon: Search, details: t("platformGuide.teamleader.step4Details", { returnObjects: true }) },
      { step: 5, title: t("platformGuide.teamleader.step5Title"), description: t("platformGuide.teamleader.step5Desc"), icon: ClipboardList, details: t("platformGuide.teamleader.step5Details", { returnObjects: true }) }
    ]
  };

  const currentSteps = steps[activeTab];

  // Color sets for timeline
  const stepColors = [
    { color: "text-blue-400", bg: "bg-blue-500/20" },
    { color: "text-purple-400", bg: "bg-purple-500/20" },
    { color: "text-orange-400", bg: "bg-orange-500/20" },
    { color: "text-green-400", bg: "bg-green-500/20" },
    { color: "text-yellow-400", bg: "bg-yellow-500/20" },
    { color: "text-cyan-400", bg: "bg-cyan-500/20" },
    { color: "text-[#23F7DD]", bg: "bg-[#23F7DD]/20" }
  ];

  return (
    <>
      <Helmet>
        <title>{t("platformGuide.pageTitle")}</title>
        <meta name="description" content={t("platformGuide.pageDescription")} />
      </Helmet>

      <div className="min-h-screen bg-[#050505]">
        {/* Header */}
        <header className="border-b border-white/10 bg-[#050505]/80 backdrop-blur-lg sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-[#23F7DD] rounded-lg flex items-center justify-center">
                <HardHat className="w-6 h-6 text-[#050505]" />
              </div>
              <span className="text-xl font-['Unbounded'] font-bold text-white">AllStillas</span>
            </Link>
            <div className="flex items-center gap-3">
              <LanguageSwitcher variant="full" className="text-white hover:bg-white/10" />
              <Link to="/">
                <Button variant="ghost" className="text-white hover:bg-white/10">
                  {t("platformGuide.home")}
                </Button>
              </Link>
              <Link to="/login">
                <Button className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]">
                  {t("platformGuide.login")}
                </Button>
              </Link>
            </div>
          </div>
        </header>

        {/* Hero */}
        <section className="py-16 px-4 text-center">
          <Badge className="bg-[#23F7DD]/20 text-[#23F7DD] border-[#23F7DD]/30 mb-4">
            <Play className="w-4 h-4 mr-2" />
            {t("platformGuide.badge")}
          </Badge>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-['Unbounded'] font-bold text-white mb-6">
            {t("platformGuide.title")} <span className="text-[#23F7DD]">{t("platformGuide.titleHighlight")}</span>?
          </h1>
          <p className="text-xl text-[#94A3B8] max-w-2xl mx-auto">
            {t("platformGuide.subtitle")}
          </p>
        </section>

        {/* Tab Navigation */}
        <section className="px-4 pb-8">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-wrap justify-center gap-2 md:gap-4">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  data-testid={`tab-${tab.id}`}
                  className={`flex items-center gap-2 px-4 md:px-6 py-3 rounded-xl font-medium transition-all ${
                    activeTab === tab.id
                      ? "bg-white/10 text-white border border-white/20"
                      : "text-[#94A3B8] hover:text-white hover:bg-white/5"
                  }`}
                >
                  <tab.icon className={`w-5 h-5 ${activeTab === tab.id ? tab.color : ""}`} />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Steps Content - Unified Timeline */}
        <section className="px-4 pb-20">
          <div className="max-w-5xl mx-auto">
            <div className="relative">
              {currentSteps.map((step, index) => {
                const colorSet = step.color ? step : stepColors[index % stepColors.length];
                
                return (
                  <div key={index} className="flex gap-6 mb-8 last:mb-0">
                    {/* Timeline */}
                    <div className="flex flex-col items-center">
                      <div className={`w-14 h-14 rounded-full ${colorSet.bg} flex items-center justify-center border-2 border-white/10`}>
                        <step.icon className={`w-7 h-7 ${colorSet.color}`} />
                      </div>
                      {index < currentSteps.length - 1 && (
                        <div className="w-0.5 h-full min-h-[60px] bg-gradient-to-b from-white/20 to-transparent mt-2" />
                      )}
                    </div>
                    {/* Content */}
                    <div className="flex-1 pb-8">
                      <div className="flex items-center gap-3 mb-2">
                        <Badge className={`${colorSet.bg} ${colorSet.color} border-0`}>
                          {t("platformGuide.step")} {step.step}
                        </Badge>
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                      <p className="text-[#94A3B8] mb-1">{step.description}</p>
                      {step.details && Array.isArray(step.details) && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {step.details.map((detail, i) => (
                            <Badge key={i} variant="outline" className="border-white/10 text-[#94A3B8]">
                              <CheckCircle2 className="w-3 h-3 mr-1 text-[#23F7DD]" />
                              {detail}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 bg-gradient-to-t from-[#23F7DD]/10 to-transparent">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-['Unbounded'] font-bold text-white mb-4">
              {t("platformGuide.ctaTitle")}
            </h2>
            <p className="text-[#94A3B8] mb-8">
              {t("platformGuide.ctaSubtitle")}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/stillasfirma-oslo">
                <Button size="lg" className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold">
                  <Building2 className="w-5 h-5 mr-2" />
                  {t("platformGuide.forBusiness")}
                </Button>
              </Link>
              <Link to="/stillasjobb-oslo">
                <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10">
                  <HardHat className="w-5 h-5 mr-2" />
                  {t("platformGuide.forWorkers")}
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-white/10 py-8 px-4">
          <div className="max-w-5xl mx-auto text-center text-[#64748B]">
            <p>{t("platformGuide.footer")}</p>
          </div>
        </footer>
      </div>
    </>
  );
}
